import { getManifestI18n } from "./manifestI18n.js";
import {
  readClientViewLocaleCookie,
  writeClientViewLocaleCookie,
} from "./clientViewPreferences.js";
import {
  PLATFORM_FALLBACK_LOCALE,
  REGISTRY_LOCALES,
  normalizeRegistryLocale,
} from "./localeRegistry.js";

export const SUPPORTED_LOCALES = REGISTRY_LOCALES;
export const DEFAULT_LOCALE = PLATFORM_FALLBACK_LOCALE;
export const FALLBACK_LOCALE = PLATFORM_FALLBACK_LOCALE;

export function normalizeLocale(value) {
  return normalizeRegistryLocale(value);
}

export function localeToLanguage(locale) {
  return normalizeLocale(locale).split("-")[0];
}

function readBrowserLocale() {
  if (typeof navigator === "undefined") {
    return null;
  }
  return navigator.language || navigator.languages?.[0] || null;
}

function readStoredLocale(storageKey) {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function readManifestDefaultLocale(manifest) {
  const i18n = getManifestI18n(manifest);
  if (i18n?.defaultLocale) {
    return i18n.defaultLocale;
  }
  return (
    manifest?.locale ||
    manifest?.defaultLocale ||
    manifest?.settings?.locale ||
    manifest?.settings?.defaultLocale ||
    manifest?.seo?.defaultLocale ||
    null
  );
}

function constrainClientViewLocale(locale, manifest) {
  const normalized = normalizeLocale(locale);
  if (!manifest) {
    return normalized;
  }
  const manifestI18n = getManifestI18n(manifest);
  if (manifestI18n.supportedLocales.includes(normalized)) {
    return normalized;
  }
  return manifestI18n.defaultLocale;
}

/**
 * @param {object} options
 * @param {"platform"|"clientView"} options.surface
 * @param {string|null} [options.urlLocale]
 * @param {string|null} [options.storedLocale]
 * @param {object|null} [options.manifest]
 * @param {object|null} [options.accountPreferences]
 */
export function resolveLocale({
  surface,
  urlLocale = null,
  storedLocale = null,
  manifest = null,
  accountPreferences = null,
}) {
  const browserLocale = readBrowserLocale();
  const manifestI18n = manifest ? getManifestI18n(manifest) : null;

  if (surface === "clientView") {
    if (urlLocale) {
      return constrainClientViewLocale(urlLocale, manifest);
    }
    const persistedLocale = readPersistedClientViewLocale() || storedLocale;
    if (persistedLocale) {
      return constrainClientViewLocale(persistedLocale, manifest);
    }
    return constrainClientViewLocale(readManifestDefaultLocale(manifest) || DEFAULT_LOCALE, manifest);
  }

  const candidates = [
    urlLocale,
    accountPreferences?.locale,
    storedLocale,
    browserLocale,
    DEFAULT_LOCALE,
  ];
  for (const candidate of candidates) {
    if (candidate) {
      const resolved = normalizeLocale(candidate);
      if (manifestI18n?.supportedLocales?.length && !manifestI18n.supportedLocales.includes(resolved)) {
        continue;
      }
      return resolved;
    }
  }
  return DEFAULT_LOCALE;
}

export const LOCALE_STORAGE_KEY = "midpoint_locale";

export function readLocaleFromStorage() {
  return readStoredLocale(LOCALE_STORAGE_KEY);
}

export function readPersistedClientViewLocale() {
  return readClientViewLocaleCookie() || readLocaleFromStorage();
}

export function writeLocaleToStorage(locale) {
  const normalized = normalizeLocale(locale);
  writeClientViewLocaleCookie(normalized);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
  } catch {
    // ignore storage failures; cookie still persists the preference
  }
}
